import { buildGeometryAudit, type ValidationReport } from '@photovalidator/core';
import { el } from '../ui/dom.js';

/**
 * Developer panel, available only with `?debug=1`.
 *
 * It shows the raw numbers the verdict was built from — handy while tuning
 * profile thresholds, and deliberately invisible to ordinary users.
 */

export const createDebugPanel = (
  report: ValidationReport | undefined,
  extra: Record<string, unknown>,
): HTMLElement => {
  const payload: Record<string, unknown> = { ...extra };

  if (report) {
    const geometry = report.metrics.geometry;
    payload['verdict'] = report.overall.verdict;
    payload['counts'] = report.overall.counts;
    payload['engine'] = report.engine;
    payload['processingMs'] = report.processingMs;
    payload['image'] = report.image;
    payload['faceCount'] = report.metrics.faces.faces.length;
    payload['detector'] = report.metrics.faces.source;
    if (geometry) {
      payload['geometry'] = {
        headHeightRatio: geometry.headHeightRatio,
        headTopSource: geometry.headTopSource,
        headTopConfidence: geometry.headTopConfidence,
        eyeLineFromTop: geometry.eyeLineFromTop,
        eyeLineFromBottom: geometry.eyeLineFromBottom,
        horizontalCenterOffset: geometry.horizontalCenterOffset,
        roll: geometry.roll,
        yaw: geometry.yaw,
        pitch: geometry.pitch,
        poseSource: geometry.poseSource,
        eyeAspectRatio: geometry.eyeAspectRatio,
        margins: geometry.margins,
        landmarks: geometry.landmarks.length,
        faceBox: geometry.faceBox,
        headBox: geometry.headBox,
      };
    }
    payload['quality'] = report.metrics.quality ?? null;
    payload['background'] = report.metrics.background ?? null;
    payload['rules'] = report.checks.map((check) => ({
      id: check.id,
      status: check.status,
      severity: check.severity,
      value: check.value,
      unit: check.unit,
      official: check.official ?? null,
      measurement: check.measurement ?? null,
      policy: check.policy ?? null,
      policyViolation: check.details?.['policyViolation'] === true,
      confidence: check.confidence,
      durationMs: check.durationMs ?? null,
      fixability: check.fixability,
    }));
    payload['measurements'] = report.measurements.map((measurement) => ({
      id: measurement.id,
      status: measurement.status,
      shapes: measurement.geometry.length,
      value: measurement.value,
      official: measurement.official ?? null,
      tolerance: measurement.measurement?.tolerance ?? null,
    }));
    payload['timing'] = {
      totalMs: report.processingMs,
      checksMs: report.checks.reduce((sum, check) => sum + (check.durationMs ?? 0), 0),
      slowest: [...report.checks]
        .sort((a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0))
        .slice(0, 5)
        .map((check) => `${check.id}: ${check.durationMs ?? 0} ms`),
    };
    payload['problems'] = report.problems;
  }

  return el('details', { class: 'pv-details', open: true }, [
    el('summary', { text: 'DEBUG: сырые результаты валидатора' }),
    geometryBlock(report),
    el('pre', { class: 'pv-debug', text: JSON.stringify(payload, null, 2) }),
  ]);
};

/**
 * Geometry audit (`?debug=1`).
 *
 * The comparison view for "our number vs the stationary validator's number".
 * Everything is printed in the **original image** coordinate system, in pixels,
 * with `head height = chin − head top` shown as an identity — so a wrong
 * coordinate system, a wrong head top or a wrong chin is visible at a glance.
 */
const geometryBlock = (report: ValidationReport | undefined): HTMLElement => {
  const audit = report ? buildGeometryAudit(report) : null;
  /**
   * Calibration hook: with `?debug=1` the audit is also published as JSON, so the
   * geometry calibration suite can compare expected vs actual numbers without
   * scraping the rendered text. Debug-only, never part of the product surface.
   */
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>)['__PV_GEOMETRY_AUDIT__'] = audit;
  }
  const lines: string[] = [];
  if (audit) {
    lines.push(`Image: ${audit.imageSize.width} × ${audit.imageSize.height}`);
    for (const row of audit.rows) {
      if (row.key === 'image') continue;
      const value =
        row.unit === 'px'
          ? String(row.pixels ?? '—')
          : row.unit === 'deg'
            ? `${(row.degrees ?? 0).toFixed(1)}°`
            : `${(row.pixels ?? 0).toFixed(1)} %`;
      lines.push(`${row.label}: ${value}${row.note ? `  (${row.note})` : ''}`);
    }
    const { values } = audit;
    if (values.headTopPx !== undefined && values.chinPx !== undefined && values.headHeightPx !== undefined) {
      lines.push(`HEAD TOP ${values.headTopPx} → CHIN ${values.chinPx} = ${values.headHeightPx} px`);
    }
    lines.push(`head top source: ${audit.headTopSource ?? 'n/a'} (confidence ${audit.headTopConfidence ?? '—'})`);
    if (audit.headTopNotes.length > 0) {
      lines.push(`head top notes: ${audit.headTopNotes.join(', ')}`);
    }
  }
  return el('div', { class: 'pv-geometry-audit' }, [
    el('p', { class: 'pv-small pv-muted', text: 'GEOMETRY (координаты оригинального изображения)' }),
    el('pre', { class: 'pv-debug pv-geometry-audit__text', text: lines.join('\n') }),
  ]);
};
